<template lang="pug">
    v-flex
      v-snackbar(:value="!!templatesErrorMessage" color="error")
        div {{ templatesErrorMessage }}
      v-snackbar(:value="!!templatesSuccessMessage" color="success")
        div {{ templatesSuccessMessage }}
      //- The dialog prompting the user to confirm overriding changes
      v-dialog(v-model="userConfirm" persistent max-width="480")
        v-card
          title-bar(dark) Unsaved Changes
          v-card-text Are you sure you want to override the unsaved changes to you template body?
          v-card-actions
            v-spacer
            v-btn(flat @click="setDesiredTemplateByUser") Yes
            v-btn.primary(@click="userConfirm = false") No
      //- The modal prompting the user to enter the new folder name
      v-dialog(v-model="isNewFolderModalOpen" persistent max-width="480")
        v-card
          title-bar(dark) Add New Folder
          v-card-text
            v-text-field(
              v-model="newFolderName"
              label="Folder Name"
              :error-messages="newFolderErrorMessage"
            )
            v-textarea(
              v-model="newFolderDescription"
              auto-grow
              rows="1"
              label="Description"
            )
          v-card-actions
            v-spacer
            v-btn(flat @click="handleClickCancel") Cancel
            v-btn.primary(
              :disabled="!newFolderName.trim().length"
              @click="handleClickCreateTemplateFolder"
            ) OK

      //- Template picker and editor appear in slide drawer
      slide-drawer(v-if="useSlideDrawer" :value="value" @input="$emit('input', false)" :default-cancel="false" ref="slideDrawerPicker")
        template(slot="title") Choose a template
        template(slot="content")
          v-container.mt-0.prevent-scroll
            slot(name="header")
              p Select the template you want to use for your {{ communicationType.toLowerCase() }}
            v-text-field(
              solo
              prepend-inner-icon="search"
              v-model="searchValue"
              placeholder="Search for templates"
            )
            v-btn.ml-0(
              data-cy="button-new-template"
              color="primary" @click="setTemplatePreview()")
              span New Template
            v-btn(flat color="primary" outline @click="isNewFolderModalOpen = true")
              span New Folder
            v-progress-linear.my-0(v-if="showLoadingBar" indeterminate)
            shallow-tree-view.display-template-folders(
              v-if="displayTemplates.length"
              v-model="displayTemplates"
              :active="activeTemplateFolderIds"
              :desiredTemplate="template"
              :hasPreview="true"
              :lookingForTemplateType="communicationType"
              :foldersDisabled="searchValue.length > 2"
              @child-clicked="setDesiredTemplate"
              @item-clicked="fetchTemplates($event)"
              @child-preview-clicked="setTemplatePreview"
              @deleteTemplate="handleRemoveResourceModal"
            )
            div.display-template-folders.no-result.text-lg-center.title.pt-5(
              v-else
            ) No results
        template(slot="actions")
          v-btn(flat color="primary" @click="$refs.slideDrawerPicker.handleDismiss()") Ok

      //- Template picker appears inline
      div(v-else)
        slot(name="header")
          p Select the template you want to use for your {{ communicationType.toLowerCase() }}
        v-text-field(
          solo
          prepend-inner-icon="search"
          v-model="searchValue"
          placeholder="Search for templates"
        )
        v-btn.ml-0(
          data-cy="button-new-template"
          color="primary" @click="setTemplatePreview();")
          span New Template
        v-btn(flat color="primary" outline @click="isNewFolderModalOpen = true")
          span New Folder
        v-progress-linear.my-0(v-if="showLoadingBar" indeterminate)
        shallow-tree-view.display-template-folders(
          v-if="displayTemplates.length"
          v-model="displayTemplates"
          :active="activeTemplateFolderIds"
          :desiredTemplate="template"
          :hasPreview="true"
          :lookingForTemplateType="communicationType"
          :foldersDisabled="searchValue.length > 2"
          @child-clicked="setDesiredTemplate"
          @item-clicked="fetchTemplates($event)"
          @child-preview-clicked="setTemplatePreview"
          @deleteTemplate="handleRemoveResourceModal"
        )
        div.display-template-folders.no-result.text-lg-center.title.pt-5(
          v-else
        ) No results

      template-slide-drawer(
        :value="templatePreview"
        @change="templateSaved"
        @deleteTemplate="handleRemoveResourceModal(templatePreview)"
      )

      remove-resource-modal(
        v-model="showDeleteTemplateDialog"
        action="delete"
        resource-singular="template"
        resource-plural="templates"
        remove-amount="single"
        :single-amount-message="deleteConfirmationMessage"
        @close="onRemoveResourceModalClose")
</template>
<script>
import { createNamespacedHelpers, mapActions, mapState } from 'vuex';
import { debounce } from '@/Core/utilities';
import TitleBar from '@/Core/components/TitleBar';
import ShallowTreeView from '@/Core/components/ShallowTreeView';
import SlideDrawer from '@/Core/components/SlideDrawer';
import toggleSlideDrawer from '@/Core/mixins/toggle-slide-drawer';
import TemplateEditor from '@/Campaigns/components/TemplateEditor';
import RemoveResourceModal from '@/Tasks/components/RemoveResourceModal';
import extractTextFromHtml from '@/Text/utilities/extractTextFromHtml';
import TemplateSlideDrawer from '@/Campaigns/components/TemplateSlideDrawer';

const {
  mapState: mapTemplatesState,
  mapMutations: mapTemplatesMutations,
  mapActions: mapTemplatesActions,
} = createNamespacedHelpers('templates');

const {
  mapActions: mapCampaignActionActions,
} = createNamespacedHelpers('campaignAction');

const {
  mapMutations: mapConversationsMutations,
} = createNamespacedHelpers('conversations');

const regexPattern = "<img[^>]*src=['\"]([^'\"]*)['\"][^>]*>";
const IMG_REGEX = new RegExp(regexPattern,'g');

export default {
  components: {
    ShallowTreeView,
    TitleBar,
    SlideDrawer,
    TemplateSlideDrawer,
    RemoveResourceModal,
  },
  mixins: [
    toggleSlideDrawer,
  ],
  props: {
    communicationType: {
      validator(value) {
        // The value must match one of these strings
        return ['Text', 'Email'].indexOf(value) !== -1;
      },
    },
    useSlideDrawer: {
      type: Boolean,
      default: false,
    },
    slideInEditor: {
      type: Boolean,
      default: false,
    },
    /**
     * The desired template
     */
    template: Object,
    value: {
      type: Boolean,
      default: true,
    },
    columnWidth: {
      type: String,
      default: 'xs12',
    },
  },
  data() {
    return {

      templatePreview: {},

      activeTab: 'templates',
      activeTemplateFolderIds: [],
      userConfirm: false,
      clickedTemplate: {},
      searchValue: '',
      // Variables to add new folder
      isNewFolderModalOpen: false,
      newFolderName: '',
      newFolderDescription: '',
      newFolderErrorMessage: '',

      showDeleteTemplateDialog: false,
      removeCampaignName: '',
      isUpdateTemplateFolders: true,
    };
  },

  computed: {
    ...mapTemplatesState([
      'displayTemplates',
      'isEditing',
      'templatesSuccessMessage',
      'templatesErrorMessage',
      'templateFoldersInProgress',
    ]),

    /**
     * Computes which vuex store we want to use to fecth data and functions.
     */
    desiredTemplate() {
      return Object.assign({}, this.template);
    },
    blankTemplate() {
      return {
        template_type: this.communicationType,
        body: '',
        files: [],
      };
    },
    deleteConfirmationMessage() {
      if (!this.removeCampaignName) return;
      return `Are you sure you want to delete the template "${this.removeCampaignName}"?`;
    },
    showLoadingBar() {
      return !this.displayTemplates.length && this.templateFoldersInProgress
    },
  },

  watch: {
    /**
     * Opens the particular folder that the current selected template is in
     * Only works if we haven't input anything in the search bar (template-folder-level)
     */
    template: {
      handler(value) {
        if (this.searchValue.length < 3) {
          if (!this.activeTemplateFolderIds.includes(value.template_folder_id)) {
            this.activeTemplateFolderIds.push(value.template_folder_id);
          }
          this.fetchTemplates({ id: value.template_folder_id });
        }
      },
      deep: true,
    },
    // Update the search string on debounce
    searchValue(value) {
      debounce(this.updateSearchString, [value]);
    },
    value: {
      immediate: true,
      handler(newValue) {
        if (!newValue) return;

        this.resetSearchState();
        this.getTemplatesAndSelectedFolder(false);
        this.reset();
      }
    },
  },

  methods: {
    ...mapTemplatesMutations([
      'assignTemplatesToFolder',
      'addQuery',
      'setTemplateToEdit',
      'setShowDrawerEdit',
    ]),
    ...mapTemplatesActions([
      'setEditing',
      'getAllTemplateFolders',
      'getTemplatesInFolder',
      'searchTemplates',
      'createTemplateFolder',
      'checkDuplicateTemplate',
      'removeTemplate',
      'resetSearch',
    ]),
    ...mapCampaignActionActions([
      'updateTemplate',
      'createTemplate',
    ]),
    ...mapConversationsMutations(['setTextImages']),

    setTemplatePreview(template = null) {

      if (!template) {
        template = this.blankTemplate;
        this.setEditing(true);
      } else {
        this.setEditing(false);
      }
      this.setTemplateToEdit(template);
      this.setShowDrawerEdit(true);
      // Create a duplicate object to remove assignment
      this.templatePreview = Object.assign({}, template);
    },

    /**
     * Function to get the children of a particular folder and assign it to that folder.
     *
     * @param {Object} folder, The template folder that we want to fetch its children
     */
    async fetchTemplates(folder) {
      const templates = await this.getTemplatesInFolder({
        templateFolder: folder,
        type: this.communicationType,
      });
      this.assignTemplatesToFolder({ desiredFolder: folder, templates });
    },
    reset() {
      this.searchValue = '';
    },
    // Function to emit the desired template back to its parent
    setDesiredTemplate(desiredTemplate) {
      if (this.communicationType === 'Text') {
        // replace image tags from text body with alias
        const newTemplateBody = desiredTemplate.body.replace(IMG_REGEX, index => {
          let parser = new DOMParser();
          let doc = parser.parseFromString(index, 'text/html');
          return this.insertImage(doc.body.getElementsByTagName("img")[0]);
        });
        // strip out remaining HTML tags in body
        // before sending desired template to parent component
        const textOnlyBody = extractTextFromHtml(newTemplateBody);
        this.$emit('change', Object.assign({}, desiredTemplate, { body: textOnlyBody }));
      } else {
        this.$emit('change', desiredTemplate);
      }
    },

    insertImage(imageData) {
      const imageAlias = this.generateImageAlias();
      const img = {
        alias: imageAlias,
        url: imageData.src,
      };
      this.setTextImages(img);
      return imageAlias;
    },

    generateImageAlias() {
      const timeStamp = new Date().getTime() + Math.floor(Math.random() * 100);
      return `{{img_media_${timeStamp}}}`;
    },

    /*
     * This closes the modal, sets editing to be false,
     * and sets the desired template
     */
    setDesiredTemplateByUser() {
      this.userConfirm = false;
      this.setEditing(false);
      this.setDesiredTemplate(this.clickedTemplate);
    },

    /**
     * Gets the templates according to the search string, and maps it to their
     * corresponding folder
     *
     * @param {String} searchString, The desired search string
     */
    async updateSearchString(searchString) {
      if (!searchString.length) {
        this.resetSearchState();
      } else if (searchString.length > 2) {
        this.activeTemplateFolderIds = [];
        await this.searchTemplates({
          searchValue: searchString,
          type: this.communicationType,
        });
        this.openAllPanels(this.displayTemplates);
      }
    },

    // TODO CS: May be preferable to find/replace the existing
    // template in the list to avoid regeneration, but
    // a shortcut taken here for simplicity of implementation.
    // Can circle back later.
    templateSaved(savedTemplate) {
      this.getTemplatesAndSelectedFolder();
    },

    /**
     * Function to re-initialize the current folder treeview in to the default:
     * Displaying a list of folders and expanding the current selected template's folder
     */
    async getTemplatesAndSelectedFolder(forceReload = true) {
      if (!forceReload && this.displayTemplates.length) return;

      await this.getAllTemplateFolders(this.isUpdateTemplateFolders);
      this.activeTemplateFolderIds = [];
      if (!this.value.template_folder_id) return;
      this.activeTemplateFolderIds.push(this.value.template_folder_id);
      this.fetchTemplates({ id: this.value.template_folder_id });
    },
    /**
     * Opens all panels, used in conjunction with searching on a template
     */
    openAllPanels(items) {
      const tempArr = [];
      for (let i = 0; i < items.length; i++) { tempArr.push(items[i].id); }
      this.activeTemplateFolderIds = tempArr;
    },
    /**
     * Helper function to create the new folder
     */
    async handleClickCreateTemplateFolder() {
      const error = await this.createTemplateFolder({
        name: this.newFolderName,
        description: this.newFolderDescription,
      });
      if (error) {
        this.newFolderErrorMessage = error.message;
      } else {
        this.newFolderErrorMessage = '';
        this.newFolderName = '';
        this.newFolderDescription = '';
        this.searchValue = '';
        await this.getTemplatesAndSelectedFolder();
        this.isNewFolderModalOpen = false;
      }
    },
    /**
     * Helper function to cancel the creation of the new folder
     */
    handleClickCancel() {
      this.isNewFolderModalOpen = false;
      this.newFolderName = '';
      this.newFolderDescription = '';
      this.newFolderErrorMessage = '';
    },

    handleRemoveResourceModal(template) {
      this.showDeleteTemplateDialog = true;
      this.removeCampaignId = template.id;
      this.removeCampaignName = template.description;
    },

    onRemoveResourceModalClose(isRemovingResource) {
      this.showDeleteTemplateDialog = false;
      if (isRemovingResource) {
        this.removeTemplate(this.removeCampaignId);
        this.getTemplatesAndSelectedFolder();
      }
    },
    
    resetSearchState() {
      this.activeTemplateFolderIds = [];
      this.resetSearch();
    },
  },
};
</script>
<style lang="scss" scoped>
  .display-template-folders {
    overflow-y: auto;
    height: 80%;
    margin: 12px -5px -5px -5px !important;
    padding: 5px !important;
    margin-top: 12px !important;
    max-width: 600px;
    &.no-result {
      color: var(--v-secondary-lighten3);
    }
  }
  .prevent-scroll {
    height: 100%;
    overflow-y: hidden;
  }
</style>
